import React, { Component } from "react";
import CartContext from "./CartContext";
import axios from "axios";
import { Card, Row, Col, Table, Spinner } from "react-bootstrap";
import { Pie, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

class OrdersDashboard extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      summary: {
        totalOrders: 0,
        completedOrders: 0,
        pendingOrders: 0,
        cancelledOrders: 0,
        totalRevenue: 0,
      },
      statusData: [],
      paymentData: [],
      deliveryData: [],
      salesTrend: [],
      topProducts: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    const { storeId } = this.context;

    try {
      this.setState({ loading: true });

      const [
        summaryRes,
        statusRes,
        paymentRes,
        deliveryRes,
        salesRes,
        topProductsRes,
      ] = await Promise.all([
        axios.get(process.env.REACT_APP_API + `orders/summary/${storeId}`),
        axios.get(process.env.REACT_APP_API + `orders/status-count/${storeId}`),
        axios.get(
          process.env.REACT_APP_API + `orders/payment-status-count/${storeId}`
        ),
        axios.get(
          process.env.REACT_APP_API + `orders/delivery-method-count/${storeId}`
        ),
        axios.get(
          process.env.REACT_APP_API + `orders/sales-trend/${storeId}?days=30`
        ),
        axios.get(process.env.REACT_APP_API + `orders/top-products/${storeId}`),
      ]);

      this.setState({
        summary: summaryRes.data || this.state.summary,
        statusData: statusRes.data || [],
        paymentData: paymentRes.data || [],
        deliveryData: deliveryRes.data || [],
        salesTrend: salesRes.data || [],
        topProducts: topProductsRes.data || [],
      });
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      loading,
      summary,
      statusData,
      paymentData,
      deliveryData,
      salesTrend,
      topProducts,
    } = this.state;

    if (loading) {
      return (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    // Charts data
    const orderStatusChart = {
      labels: statusData.map((s) => s?.status || "Unknown"),
      datasets: [
        {
          label: "Orders",
          data: statusData.map((s) => s?.count || 0),
          backgroundColor: ["#007bff", "#28a745", "#dc3545", "#ffc107"],
        },
      ],
    };

    const paymentStatusChart = {
      labels: paymentData.map((p) => p?.paymentStatus || "Unknown"),
      datasets: [
        {
          label: "Payments",
          data: paymentData.map((p) => p?.count || 0),
          backgroundColor: ["#17a2b8", "#28a745", "#ffc107", "#dc3545"],
        },
      ],
    };

    const deliveryMethodChart = {
      labels: deliveryData.map((d) => d?.deliveryMethod || "Unknown"),
      datasets: [
        {
          label: "Delivery",
          data: deliveryData.map((d) => d?.count || 0),
          backgroundColor: [
            "#007bff",
            "#28a745",
            "#ffc107",
            "#dc3545",
            "#6f42c1",
          ],
        },
      ],
    };

    const salesTrendChart = {
      labels: salesTrend.map((s) =>
        s?.date ? new Date(s.date).toLocaleDateString("en-US") : ""
      ),
      datasets: [
        {
          label: "Revenue",
          data: salesTrend.map((s) => s?.totalAmount || 0),
          fill: false,
          backgroundColor: "#007bff",
          borderColor: "#007bff",
        },
      ],
    };

    return (
      <div className="container mt-4 text-center">
        <h3>
          <b>Sales Order Summary - 360 View</b>
        </h3>
        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Card.Title>Total Orders</Card.Title>
                <Card.Text>{summary?.totalOrders || 0}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Card.Title>Completed Orders</Card.Title>
                <Card.Text>{summary?.completedOrders || 0}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Card.Title>Pending Orders</Card.Title>
                <Card.Text>{summary?.pendingOrders || 0}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Card.Title>Revenue</Card.Title>
                <Card.Text>
                  ${(summary?.totalRevenue || 0).toFixed(2)}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row className="mb-4">
          <Col md={4}>
            <Card>
              <Card.Body>
                <Card.Title>Order Status</Card.Title>
                <Pie data={orderStatusChart} />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body>
                <Card.Title>Payment Status</Card.Title>
                <Pie data={paymentStatusChart} />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body>
                <Card.Title>Delivery Method</Card.Title>
                <Bar data={deliveryMethodChart} />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Sales Trend */}
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Body>
                <Card.Title>Sales Trend (Last 30 Days)</Card.Title>
                <Line data={salesTrendChart} />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Products */}
        <Row>
          <Col md={12}>
            <Card>
              <Card.Body>
                <Card.Title>Top Products</Card.Title>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Quantity Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.length > 0 ? (
                      topProducts.map((p, idx) => (
                        <tr key={idx}>
                          <td>{p?.productName || "Unknown"}</td>
                          <td>{p?.quantitySold || 0}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center">
                          No products sold
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default OrdersDashboard;
